import { lazy, Suspense, useEffect } from "react";
import "./growth.css";

const GrowthJournal = lazy(() => import("./GrowthJournal.jsx"));

const title = "Apping Growth | Hunter, Compass & Closer";
const description = "Explore Apping Growth: Hunter for buyer discovery, Compass for content direction, and Closer for relationship context.";
const navigation = [
  ["Product", "/product"],
  ["Market Entry", "/market-entry"],
  ["Who It’s For", "/who-its-for"],
  ["Growth", "/growth"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

export function GrowthHeader() {
  return (
    <header className="growth-header">
      <a className="growth-brand" href="/" aria-label="Apping homepage"><img src="/apping-logo.png" alt="Apping Technology" width="985" height="999" /></a>
      <nav aria-label="Primary navigation">{navigation.map(([label, href]) => <a className={href === "/growth" ? "is-active" : ""} href={href} key={href}>{label}</a>)}</nav>
      <a className="growth-header-contact" href="mailto:info@appingtechnology.com?subject=Apping%20Growth%20enquiry">Talk to Apping <span aria-hidden="true">↗</span></a>
    </header>
  );
}

export function GrowthFooter() {
  return (
    <footer className="growth-footer">
      <div className="growth-container growth-footer-grid">
        <a href="/">Apping Technology</a>
        <nav aria-label="Footer navigation"><a href="/product">Sales OS</a><a href="/growth">Apping Growth</a><a href="/insights">Insights</a><a href="/about">About</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a></nav>
        <a href="mailto:info@appingtechnology.com">info@appingtechnology.com</a>
      </div>
    </footer>
  );
}

export default function GrowthPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") || "";
    document.title = title;
    meta?.setAttribute("content", description);
    document.documentElement.dataset.page = "growth";
    return () => {
      document.title = previousTitle;
      meta?.setAttribute("content", previousDescription);
      delete document.documentElement.dataset.page;
    };
  }, []);

  return (
    <div className="growth-page">
      <a className="growth-skip" href="#growth-main">Skip to content</a>
      <GrowthHeader />
      <main id="growth-main">
        <section className="growth-hero" aria-labelledby="growth-title">
          <img className="growth-hero-image" src="/images/apping-growth/hero-ai.jpg" alt="" width="1920" height="1080" fetchPriority="high" />
          <div className="growth-container growth-hero-inner">
            <p className="growth-eyebrow">Apping Growth</p>
            <h1 id="growth-title">Find leads. Plan content. Keep clients.</h1>
            <p className="growth-lede">Hunter helps find buyers. Compass and Closer extend the vision into content and relationship intelligence.</p>
            <a className="growth-action" href="#hunter">Explore Hunter <span aria-hidden="true">↘</span></a>
          </div>
        </section>

        <section className="growth-intro" aria-label="Apping Growth overview">
          <div className="growth-container growth-intro-inner">
            <p>Different moments in growth need different tools.</p>
            <p>Apping Growth brings buyer discovery, content direction and relationship context into one broader conversation.</p>
          </div>
        </section>

        <section className="growth-hunter" id="hunter" aria-labelledby="hunter-title">
          <div className="growth-container growth-hunter-grid">
            <div className="growth-hunter-copy">
              <p className="growth-eyebrow">Our current focus</p>
              <h2 id="hunter-title">Hunter</h2>
              <p className="growth-subhead">Find the buyers who matter.</p>
              <p>Start with a target market. Hunter helps B2B teams identify relevant companies and the people behind buying decisions.</p>
              <a className="growth-text-link" href="mailto:info@appingtechnology.com?subject=Hunter%20enquiry">Ask about Hunter <span aria-hidden="true">↗</span></a>
            </div>
            <figure className="growth-hunter-art"><img src="/images/apping-growth/viz-hunter.jpg" alt="Concentric orange circles representing buyer discovery" width="1024" height="768" loading="lazy" decoding="async" /></figure>
          </div>
        </section>

        <section className="growth-others" aria-labelledby="growth-others-title">
          <div className="growth-container">
            <h2 id="growth-others-title">More ways to move growth forward.</h2>
            <div className="growth-product-pair">
              <article className="growth-product">
                <img src="/images/apping-growth/viz-compass.jpg" alt="Illustrated planning calendar in Apping's plum palette" width="1024" height="768" loading="lazy" decoding="async" />
                <div><h3>Compass</h3><p>Content intelligence to help teams decide what to create around the topics their market cares about.</p></div>
              </article>
              <article className="growth-product">
                <img src="/images/apping-growth/viz-closer.jpg" alt="Connected conversation shapes representing relationship context" width="1024" height="768" loading="lazy" decoding="async" />
                <div><h3>Closer</h3><p>Relationship intelligence to help teams carry customer and conversation context into the next interaction.</p></div>
              </article>
            </div>
          </div>
        </section>

        <Suspense fallback={null}><GrowthJournal /></Suspense>

        <section className="growth-bridge" aria-labelledby="growth-bridge-title"><div className="growth-container growth-bridge-grid"><div><h2 id="growth-bridge-title">Looking for the broader system?</h2></div><div><p>Sales OS brings market discovery, account focus and private sales intelligence into a connected workflow for B2B growth.</p><a className="growth-text-link" href="/product">Explore Sales OS <span aria-hidden="true">↗</span></a></div></div></section>
      </main>
      <GrowthFooter />
    </div>
  );
}
